import { Button, Col, Form, Row, DatePicker, Select, Table, Empty, notification } from 'antd'
import React, { useState, useEffect } from 'react'
import type { ColumnsType } from "antd/es/table";
import { ReactComponent as NoPayoutIcon } from "../../../src/assets/images/no-payout.svg";
import "../../../src/App.scss";
import "../PayoutSetting/PayoutSetting.scss";
import { useForm } from 'antd/es/form/Form'
import OIButton from 'shared/ComponentsOI/OIButton/OIButton';
import moment from 'moment';
import { getEnumList, INRFormatter } from 'shared/services/Utility';
import { useSelector } from 'react-redux';
import { RootState } from 'shared/redux/store';
import API_SERVICE from "shared/services/api-service";
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';

const { RangePicker } = DatePicker;

const dateFormat = "DD-MM-YYYY";

const columns: ColumnsType<any> = [
  {
    title: "Product Name",
    dataIndex: "productName",
    key: "productName",
    sorter: (a, b) => {
      const aVal = (a.productName || '').toString();
      const bVal = (b.productName || '').toString();
      if (!aVal && !bVal) return 0;
      if (!aVal) return -1;
      if (!bVal) return 1;
      return aVal.localeCompare(bVal);
    },
    showSorterTooltip: false
  },
  {
    title: "Lender Name",
    dataIndex: "lenderBusinessName",
    key: "lenderBusinessName",
    sorter: (a, b) => {
      const aVal = (a.lenderBusinessName || '').toString();
      const bVal = (b.lenderBusinessName || '').toString();
      if (!aVal && !bVal) return 0;
      if (!aVal) return -1;
      if (!bVal) return 1;
      return aVal.localeCompare(bVal);
    },
    sortDirections:['ascend', 'descend'],
    showSorterTooltip: false
  },
  {
    title: "Effective Date",
    dataIndex: "effectiveDate",
    key: "effectiveDate",
    sorter: (a, b) => {
      const aDate = a.effectiveDate ? moment(a.effectiveDate, "DD-MM-YYYY").valueOf() : 0;
      const bDate = b.effectiveDate ? moment(b.effectiveDate, "DD-MM-YYYY").valueOf() : 0;
      return aDate - bDate;
    },
    sortDirections:['ascend', 'descend'],
    showSorterTooltip: false
  },
  {
    title: "Slabs (Range)",
    dataIndex: "slabsrange",
    key: "slabsrange",
  },
  {
    title: "Payout",
    dataIndex: "payoutAs",
    key: "payoutAs",
  },
  {
    title: "Payout Type",
    dataIndex: "payoutType",
    key: "payoutType",
    sorter: (a, b) => {
      const aVal = (a.payoutType || '').toString();
      const bVal = (b.payoutType || '').toString();
      if (!aVal && !bVal) return 0;
      if (!aVal) return -1;
      if (!bVal) return 1;
      return aVal.localeCompare(bVal);
    },
    showSorterTooltip: false
  },
  {
    title: "Configured Date",
    dataIndex: "creationDate",
    key: "creationDate",
    sorter: (a, b) => {
      const aDate = a.creationDate ? moment(a.creationDate, "DD-MM-YYYY").valueOf() : 0;
      const bDate = b.creationDate ? moment(b.creationDate, "DD-MM-YYYY").valueOf() : 0;
      return aDate - bDate;
    },
    sortDirections:['ascend', 'descend'],
    showSorterTooltip: false
  }
];

function formatSlabRange(
  min: number | string | null | undefined,
  max: number | string | null | undefined,
  INRFormatterFn: (val: any, showSymbol?: boolean) => string = INRFormatter
): string {
  if ((min == null || min === "") && (max != null && max !== "")) {
    return `${INRFormatterFn(0, true)} - ${INRFormatterFn(max, true)}`;
  } else if ((min != null && min !== "") && (max == null || max === "")) {
    return `> ${INRFormatterFn(min, true)}`;
  } else if ((min != null && min !== "") && (max != null && max !== "")) {
    if (Number(min) === 0 && Number(max) === 0) {
      return "-";
    } else {
      return `${INRFormatterFn(min, true)} - ${INRFormatterFn(max, true)}`;
    }
  }
  return "-";
}

function PayoutHistory({connector, payoutTabKey}) {
  const { appColors }: any = useSelector((state: RootState) => state.common);
  const [reqDate, setReqDate] = useState<[moment.Moment | null, moment.Moment | null] | null>(null);
  const [filterForm] = useForm();
  const [payoutHistoryData, setPayoutHistoryData] = useState<any[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [loadingLender, setLoadingLender] = useState(false);
  const [allLenders, setAllLenders] = useState([]);
  
  const btnStylePrimary: any = { "--custColor": appColors?.appPrimaryColor }
  const { id } = useParams() as any;
    
  const connectorId = connector?.basicDetails?.connectorId || id;

  useEffect(() => {
    if (payoutTabKey === "2-2") {
      // Reset form and states when tab changes to Payout History
      filterForm.resetFields();
      setReqDate(null);
      setIsFilterApplied(false);
      fetchPayoutHistory();
    }
  }, [payoutTabKey]);

  const fetchPayoutHistory = async (filters?: any) => {
    setLoadingTable(true);
    const params: any = {
      id: connectorId,
      forConnector: true,
      isLatest: false,
      status: "ACTIVE",
      lenderBusinessId: filters?.lenderBusinessId
    };
    if (filters?.product) params.loanProductId = filters.product;
    if (filters?.startDate && Array.isArray(filters.startDate) && filters.startDate[0] && filters.startDate[1]) {
      params.fromDate = filters.startDate[0].format("YYYY-MM-DD");
      params.toDate = filters.startDate[1].format("YYYY-MM-DD");
    }
    try {
      const { data } = await API_SERVICE.getPayoutConfigList(params);
      const list = data?.payload ?? [];
      setPayoutHistoryData(
        list.map((item, idx) => ({
          key: [item.tenantId, item.loanProductId, item.effectiveFrom].every(Boolean)
            ? `${item.tenantId}-${item.loanProductId}-${item.effectiveFrom}`
            : `row-${idx}`,
          lenderBusinessName: item.lenderBusinessName,
          productName: item.productName,
          effectiveDate: item.effectiveFrom,
          slabsrange: formatSlabRange(item.minAmount, item.maxAmount),
          payoutAs:
            (item.payoutAs &&item.payoutSubType && item.payoutSubType.toUpperCase() === "AMOUNT")
              ? `${INRFormatter(item.payoutAs, true)}`
              : (item.payoutAs && item.payoutSubType && item.payoutSubType.toUpperCase() === "PERCENTAGE")
              ? `${item.payoutAs}%`
              : item.payoutAs ?? "-",
          payoutType: item.payoutType,
          creationDate: item.creationDate,
        }))
      );
    } finally {
      setLoadingTable(false);
    }
  };

  React.useEffect(() => {
    setIsFilterApplied(false);
    fetchPayoutHistory();
  }, []);

  const getAllLenders = (loanProductId) => {
    setLoadingLender(true);
    API_SERVICE.getLenders({
      status: "Active",
      loanProductId: loanProductId,
      pageNo: 1,
      pageSize: 200,
    })
    .then(({ data }) => {
      let lenders = data.payload.content.map((lender) => ({
        value: lender.lenderInfoId,
        label: lender.lenderName,
      }));
      setAllLenders(lenders);
    })
    .catch((e: any) => notification.error({ message: API_SERVICE.handleErrors(e) }))
      .finally(() => setLoadingLender(false));
  };

  const handleFilter = (values: any) => {
    setIsFilterApplied(true);
    fetchPayoutHistory(values);
  }

  const renderPayoutHistoryTable = () => {
    return (
      <>
        <h6>Configured Payouts</h6>
        <Table
          className="dynamic-pagination-checkbox"
          style={{ "--bgColor": appColors?.appPrimaryColor ?? "" } as any}
          scroll={{ x: "max-content" }}
          columns={columns}
          dataSource={payoutHistoryData}
          loading={loadingTable}
          size="small"
          pagination={{}}
          rowKey="key"
        />
      </>
    );
  };

  const emptyFallback = () => {
    return <Empty style={{margin:50}} image={<NoPayoutIcon />} description="" />;
  }

  return (
    <>
      {((payoutHistoryData.length > 0) || isFilterApplied) ? (
        <div className="content-box" style={{ marginInline: 0 }}>
          <Form layout="vertical" form={filterForm} onFinish={handleFilter}>
            <div>
              <Row gutter={[24, 24]}>
                <Col xs={24} xl={10} span={10}>
                  <div className="form-box">
                    <Form.Item
                      className="field-bg"
                      name="product"
                      style={{ width: "100%" }}
                      label="Select Product"
                      rules={[
                        {
                          required: false,
                          message: "Please select Product",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Product"
                        showSearch
                        optionFilterProp="children"
                        allowClear={true}
                        onSelect={(value) => {
                          getAllLenders(value)
                        }}
                      >
                        {getEnumList("LOAN_TYPE").map(
                          (item: { key: string; value: string }) => (
                            <Select.Option key={item.key} value={item.key}>
                              {item.value}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      className="field-bg"
                      label="Select Lender"
                      name="lenderBusinessId"
                      style={{ marginBottom: "5px", marginTop: "5px" }}
                    >
                      <Select
                        placeholder="Select Lender"
                        className="custom-select"
                        showSearch
                        optionFilterProp="label"
                        // onSearch={debounce((value) => getAllLenders(value), 1000)}
                        options={allLenders}
                        loading={loadingLender}
                      />
                    </Form.Item>

                    <label className="label-text">Select Date Range</label>
                    <Form.Item
                      name="startDate"
                      rules={[{ required: true, message: 'Please select a start date' }]}
                      label="Select Date Range"
                      labelCol={{ span: 24 }}
                    >
                      <RangePicker
                      className="custom-input"
                      placeholder={["From Date", "To Date"]}
                      value={reqDate}
                      onChange={(
                        dates: [moment.Moment | null, moment.Moment | null] | null
                      ) => setReqDate(dates)}
                      disabled={false}
                      format={dateFormat}
                      />
                    </Form.Item>
                    <Form.Item>
                      <OIButton
                        style={{ ...btnStylePrimary, width: "100%" }}
                        label="Search"
                        type="primary"
                        htmlType="submit"
                      />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} xl={14} span={14} style={{ paddingLeft: 0 }}>
                  {renderPayoutHistoryTable()}
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      )
        : (emptyFallback())
      }

    </>
  )
}

export default PayoutHistory